import { useCallback, useMemo, useState } from 'react';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core/hooks';

import useConfirmAddressQuery from '../queries/useConfirmAddress.query';
import useGetAddressUndeliverableQuery from '../queries/useGetAddressUndeliverable.query';

export enum ReviewOption {
  CORRECT = 'CORRECT',
  NEEDS_UPDATE = 'NEEDS_UPDATE',
}

interface Props {
  onClose: () => void;
}

const useReviewAddressModal = ({ onClose }: Props) => {
  const { addressId, refetch, formattedAddress, addressPayload } = useGetAddressUndeliverableQuery();
  const { confirmAddress, isLoading: isConfirming } = useConfirmAddressQuery();

  const hasEditPermission = useHasAccess([
    { permission: Permissions.EDIT_EE_DATA },
    { permission: Permissions.EDIT_PROFILE },
  ]);

  const [selectedOption, setSelectedOption] = useState<ReviewOption | null>(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const submitButtonLabel = useMemo(() => {
    if (!selectedOption) return 'Select Option';
    if (selectedOption === ReviewOption.CORRECT) return 'Confirm';
    return 'Edit Address';
  }, [selectedOption]);

  const isSubmitDisabled = !selectedOption || isConfirming;

  const handleSubmit = useCallback(async () => {
    if (selectedOption === ReviewOption.CORRECT && addressId) {
      await confirmAddress(addressId);
      onClose();
      setSelectedOption(null);
      setSuccessMessage('Current address was marked as correct!');
      setTimeout(() => {
        setIsSuccessVisible(true);
      }, 100);
    } else if (selectedOption === ReviewOption.NEEDS_UPDATE) {
      onClose();
      setSelectedOption(null);
      setIsEditModalVisible(true);
    }
  }, [selectedOption, addressId, confirmAddress, onClose]);

  const handleClose = useCallback(() => {
    onClose();
    setSelectedOption(null);
  }, [onClose]);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalVisible(false);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setIsEditModalVisible(false);
    setTimeout(() => {
      setSuccessMessage('Address has been successfully updated!');
      setIsSuccessVisible(true);
    }, 100);
  }, []);

  const handleSuccessClose = useCallback(() => {
    setIsSuccessVisible(false);
    refetch();
  }, [refetch]);

  return {
    selectedOption,
    setSelectedOption,
    isEditModalVisible,
    isSuccessVisible,
    successMessage,
    isConfirming,
    hasEditPermission,
    submitButtonLabel,
    isSubmitDisabled,
    formattedAddress,
    addressPayload,
    handleSubmit,
    handleClose,
    handleEditModalClose,
    handleEditSuccess,
    handleSuccessClose,
  };
};

export default useReviewAddressModal;
